// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Cpu, Zap, Shield, Key, Database, RefreshCw, Layers, Clock, CheckCircle2, AlertTriangle, Play
} from 'lucide-react';

export const NexoraXIncubator: React.FC = () => {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [trialTimeLeft, setTrialTimeLeft] = useState<number>(60);
  const [deploymentStatus, setDeploymentStatus] = useState<'idle' | 'processing' | 'success'>('idle');
  const [progress, setProgress] = useState<number>(0);

  // Financial Breakdown calculations for £5,000 deployment
  const depositAmount = 5000;
  const levyAmount = depositAmount * 0.01; // £50
  const charitySplit = levyAmount / 3; // £16.67 each
  const remainingAfterLevy = depositAmount - levyAmount; // £4,950
  const gavelFee = remainingAfterLevy * 0.20; // £990
  const netSettlement = remainingAfterLevy - gavelFee; // £3,960

  const subNodes = [
    "1. Automated Inventory Sink", "2. Dynamic Pricing Arbitrage", "3. Neural Sentiment Shield",
    "4. Automated Audit Trail", "5. Forensic Ledger Reconciliation", "6. Tax Reserve Routing",
    "7. eIDAS Contract Verification", "8. Compliance Lockdown", "9. Secure Document Enclave",
    "10. Autonomous Routing", "11. Fleet Latency Nullification", "12. Supply Chain Mesh",
    "13. AI-Driven Talent Acquisition", "14. Performance Synthesis", "15. NexoraX Onboarding",
    "16. Voice Matrix Integration", "17. Multilingual Sentiment", "18. Neural PR Response",
    "19. Edge-Mesh Traffic Control", "20. Host-Shift Counter-measures", "21. Quantum Key Distribution",
    "22. Workflow Orchestration", "23. Zero-Touch API Mgmt", "24. Predictive Volatility Scan",
    "25. Synthetic Persona Gen", "26. Viral Content Swarm", "27. Automated Pitch Decks",
    "28. Geothermal Server Metrics", "29. NexoraX Domain Cloaking", "30. High-Density QR Settlement"
  ];

  // 60-Second Temporal Lock Protocol
  useEffect(() => {
    if (trialTimeLeft > 0 && !isLocked) {
      const timer = setTimeout(() => setTrialTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (trialTimeLeft === 0) {
      setIsLocked(true);
    }
  }, [trialTimeLeft, isLocked]);

  const handleResetLock = () => {
    setTrialTimeLeft(60);
    setIsLocked(false);
    setDeploymentStatus('idle');
    setProgress(0);
  };

  const startDeploymentSimulation = () => {
    if (isLocked) return;
    setDeploymentStatus('processing');
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setDeploymentStatus('success');
          return 100;
        }
        return prev + 5;
      });
    }, 100);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8 space-y-8 bg-[#02050c] text-slate-100 rounded-2xl border border-amber-600/30 shadow-[0_0_50px_rgba(217,119,6,0.05)] relative overflow-hidden">
      
      {/* Visual Ambient Background element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Header and Title */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-amber-500/20 pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono text-amber-500 font-bold uppercase tracking-widest">
            <Layers className="w-4 h-4 animate-pulse" />
            <span>Node-91 Imperial Core Grid</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-sans font-black text-white uppercase tracking-wider mt-2">
            Category 91: NexoraX Incubator <span className="text-amber-500 font-mono">(30-Node Matrix)</span>
          </h2>
          <p className="text-xs text-slate-400 font-mono tracking-wide mt-1">
            Authorized deployment protocols engineered for Director Lead Director (NexoraX Digital Ltd - SC887095)
          </p>
        </div>

        {/* Temporal Lock Section */}
        <div className="flex items-center gap-3 shrink-0">
          {isLocked && (
            <button 
              onClick={handleResetLock}
              className="px-3 py-1.5 border border-amber-500/30 bg-amber-950/20 hover:bg-amber-900/40 text-amber-400 font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
            >
              Reset Session
            </button>
          )}
          <div className={`px-4 py-2 border rounded-xl font-mono text-xs font-bold tracking-widest transition-all duration-300 flex items-center gap-2 ${
            isLocked 
              ? 'border-red-600 bg-red-950/40 text-red-400 animate-pulse' 
              : 'border-amber-500 bg-amber-950/40 text-amber-500'
          }`}>
            <Clock className={`w-3.5 h-3.5 ${!isLocked ? 'animate-spin' : ''}`} />
            <span>
              {isLocked 
                ? "ACCESS SUSPENDED: TEMPORAL LOCK ENGAGED" 
                : `60-SECOND TEMPORAL LOCK: 00:${trialTimeLeft < 10 ? '0' + trialTimeLeft : trialTimeLeft}`}
            </span>
          </div>
        </div>
      </div>

      {/* Grid of the 30-Node Matrix */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {subNodes.map((node, index) => {
          const nodeNumber = index + 1;
          const isCurrentActive = activeNode === node;
          return (
            <button 
              key={node}
              disabled={isLocked || deploymentStatus === 'processing'}
              onClick={() => {
                setActiveNode(node);
                setDeploymentStatus('idle');
                setProgress(0);
              }}
              id={`node-btn-${nodeNumber}`}
              className={`p-4 border text-left rounded-xl transition-all duration-300 ${
                isLocked 
                  ? 'border-slate-800 bg-slate-900/10 text-slate-600 cursor-not-allowed'
                  : isCurrentActive 
                    ? 'border-amber-500 bg-amber-950/30 shadow-[0_0_20px_rgba(245,158,11,0.15)] text-white' 
                    : 'border-neutral-800 hover:border-amber-500/50 bg-neutral-950/60 text-slate-400 hover:text-slate-200'
              }`}
            >
              <div className="flex justify-between items-center text-[9px] font-mono tracking-widest font-bold">
                <span className={isCurrentActive ? 'text-amber-500' : 'text-slate-500'}>
                  NODE-91.{nodeNumber < 10 ? '0' + nodeNumber : nodeNumber}
                </span>
                <span className="text-[7px] px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded uppercase font-bold text-slate-400">
                  {isCurrentActive ? 'READY' : 'INIT'}
                </span>
              </div>
              <div className="text-xs font-mono font-bold mt-2 leading-snug line-clamp-1">
                {node.replace(/^\d+\.\s*/, '')}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Node Control & Interactive Extraction Panel */}
      <AnimatePresence mode="wait">
        {activeNode && (
          <motion.div 
            key={activeNode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="mt-8 p-6 bg-neutral-950 border border-amber-500/30 rounded-2xl relative"
          >
            <div className="absolute top-3 right-3 text-[9px] font-mono text-amber-500/60">
              IMMUTABLE RECORD REPLICATION ACTIVE
            </div>

            <div className="flex flex-col lg:flex-row gap-8 justify-between">
              
              {/* Left Column: Descriptions and Actions */}
              <div className="flex-1 space-y-4">
                <div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-amber-500 block">
                    Active Command Session
                  </span>
                  <h3 className="text-xl font-black text-white uppercase tracking-wider mt-1">
                    {activeNode}
                  </h3>
                </div>

                <p className="text-sm text-slate-300 leading-relaxed max-w-2xl font-mono">
                  The NexoraX Incubator authorizes secure deployment of computing assets directly mapped to 
                  the Director's global network infrastructure. The 60-Second Temporal Lock mandates fast 
                  settlement to prevent node allocation expiration. All operations are documented and mirrored 
                  instantaneously to the Google Drive NexoraX Resource Library via CloudSync-02.
                </p>

                {/* Progress bar for simulation */}
                {deploymentStatus === 'processing' && (
                  <div className="space-y-2 font-mono">
                    <div className="flex justify-between text-xs text-amber-400 font-bold">
                      <span>DEPLOYING INJECTION PROTOCOLS...</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-slate-900 h-2 border border-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="bg-amber-500 h-full transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {deploymentStatus === 'success' && (
                  <div className="p-4 border border-emerald-500/20 bg-emerald-950/20 rounded-xl flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="font-mono text-xs">
                      <div className="font-extrabold text-emerald-400 uppercase">DEPLOYMENT COMPLETED SUCCESSFULLY</div>
                      <div className="text-slate-400 mt-1">
                        NexoraX Handshake dispatched. Core registry sync verified. Ledger payload cloned to backup nodes.
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Action buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button 
                    disabled={isLocked || deploymentStatus === 'processing'}
                    onClick={startDeploymentSimulation}
                    className="flex-1 py-3 px-6 bg-amber-500 hover:bg-amber-600 disabled:bg-neutral-900 disabled:border-neutral-800 disabled:text-slate-700 border border-transparent text-black font-black text-center uppercase tracking-widest rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Play className="w-4 h-4 fill-current" />
                    <span>Initialize NexoraX deployment</span>
                  </button>
                  <a 
                    href="/marketplace" 
                    className="py-3 px-6 border border-amber-500/50 hover:border-amber-500 bg-amber-950/10 hover:bg-amber-950/20 text-amber-400 font-black text-center uppercase tracking-widest rounded-xl transition-all duration-200 block"
                  >
                    Direct Checkout
                  </a>
                </div>
              </div>

              {/* Right Column: Precise NexoraX Financial Split Dashboard */}
              <div className="w-full lg:w-96 border border-amber-500/20 bg-black/40 p-5 rounded-xl space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-800">
                  <Shield className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-mono font-bold tracking-wider text-slate-300 uppercase">
                    Tiered Extraction Protocol
                  </span>
                </div>

                <div className="space-y-3 font-mono text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Gross Node Deposit</span>
                    <span className="text-white font-extrabold">£{depositAmount.toLocaleString()}</span>
                  </div>

                  <div className="p-3 bg-amber-950/10 border border-amber-500/10 rounded-lg space-y-2">
                    <div className="flex justify-between font-bold text-amber-500">
                      <span>1% 3% Community Allocation</span>
                      <span>£{levyAmount.toFixed(2)}</span>
                    </div>
                    <div className="pl-3 text-[10px] space-y-1 text-slate-400">
                      <div className="flex justify-between">
                        <span>Charity 1 (Dunfermline)</span>
                        <span>£{charitySplit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Charity 2 (Global Core)</span>
                        <span>£{charitySplit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Charity 3 (United Kingdom)</span>
                        <span>£{(charitySplit - 0.01).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-slate-500">Remaining Balance</span>
                    <span className="text-slate-300">£{remainingAfterLevy.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between font-bold text-amber-500">
                    <span>10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)</span>
                    <span>£{gavelFee.toLocaleString()}</span>
                  </div>

                  <div className="pt-3 border-t border-slate-800 space-y-2">
                    <div className="flex justify-between font-black text-sm text-emerald-400">
                      <span>Net Settlement</span>
                      <span>£{netSettlement.toLocaleString()}</span>
                    </div>
                    <div className="text-[9px] text-slate-500 leading-snug">
                      Authorized routing output bound exclusively to Secure Payment Provider <br />
                      <span className="text-slate-400 font-bold">Sort Code: 04-**-** | Account: ******15</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
